import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Layout,
  Calendar,
  BookMarked,
  X,
  Camera,
  BookOpen,
  Building2,
  Upload,
  Image,
  FileText,
  Download,
  Target,
  FileSpreadsheet
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAttachmentUrl = (att) =>
  att.storage_type === "cloud" ? `${API}/storage/${att.filename}` : `${API}/uploads/${att.filename}`;

const BRANCHES = [
  "Carbon Valley Regional",
  "Centennial Park",
  "Erie Community",
  "Farr Regional",
  "LINC",
  "Riverside Library and Cultural Center"
];

const MEMBERS = [
  "Eaton",
  "Fort Lupton",
  "Glenn A. Jones",
  "Hudson",
  "Nantes Library (Gilcrest)",
  "Northern Plains (Ault)",
  "Platteville"
];

const PURPOSES = [
  { value: "book_display", label: "Book Display" },
  { value: "applicable_to_work", label: "Applicable to Work" },
  { value: "readers_advisory", label: "Readers Advisory" },
  { value: "collection_suggestion", label: "Collection Suggestion" }
];

const DISPLAY_TOPICS = [
  "New Arrivals",
  "Staff Picks",
  "Seasonal",
  "Holiday",
  "Award Winners",
  "Local Authors",
  "Children's Favorites",
  "Teen Reads",
  "Book Club Picks",
  "STEM",
  "History",
  "Biography",
  "Mystery",
  "Romance",
  "Science Fiction",
  "Self Help",
  "Cooking",
  "Travel",
  "Art & Photography",
  "Other"
];

const initialFormData = {
  title: "",
  topic: "",
  theme: "",
  description: "",
  location: "",
  start_date: "",
  end_date: "",
  linked_goals: [],
  notes: ""
};

const initialBookData = {
  title: "",
  author: "",
  call_number: "",
  barcode: "",
  branch: "",
  member: "",
  purpose: "book_display",
  book_id: ""
};

export const BookDisplays = ({ user, viewMode, userLookup = {} }) => {
  const [displays, setDisplays] = useState([]);
  const [books, setBooks] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addBookDialogOpen, setAddBookDialogOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [selectedDisplay, setSelectedDisplay] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [bookData, setBookData] = useState(initialBookData);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [formTab, setFormTab] = useState("info");
  const [customTopic, setCustomTopic] = useState("");
  const [uploading, setUploading] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Helper to get staff name from userLookup
  const getStaffName = (createdBy) => {
    if (!createdBy || !userLookup[createdBy]) return null;
    return userLookup[createdBy].name;
  };
  
  // Check if user can edit/delete this display (author or supervisor/admin)
  const canEditDisplay = (display) => {
    if (!display || !user) return false;
    return display.created_by === user.id || user.role === "supervisor" || user.is_admin;
  };

  useEffect(() => {
    fetchDisplays();
    fetchBooks();
    fetchGoals();
  }, []);

  const fetchDisplays = async () => {
    try {
      const response = await axios.get(`${API}/book-displays`);
      setDisplays(response.data);
    } catch (error) {
      toast.error("Failed to fetch book displays");
    } finally {
      setLoading(false);
    }
  };

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API}/books`);
      setBooks(response.data);
    } catch (error) {
      console.error("Failed to fetch books");
    }
  };

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/goals`);
      setGoals(response.data);
    } catch (error) {
      console.error("Failed to fetch goals");
    }
  };

  // Get all books for a display (both display_books and books linked from Books page)
  const getAllBooksForDisplay = (display) => {
    const allBooks = [];
    const seenBarcodes = new Set();
    
    // 1. Add books from display_books array
    if (display.display_books) {
      display.display_books.forEach(book => {
        allBooks.push(book);
        if (book.barcode) seenBarcodes.add(book.barcode);
      });
    }
    
    // 2. Add books from Books collection that are linked to this display
    const linkedBooks = books.filter(b => b.book_display_id === display.id);
    linkedBooks.forEach(book => {
      // Avoid duplicates by checking barcode
      if (!book.barcode || !seenBarcodes.has(book.barcode)) {
        allBooks.push({
          id: book.id,
          title: book.title,
          author: book.author,
          barcode: book.barcode || '',
          call_number: book.call_number || '',
          fromBooksPage: true  // Flag to identify source
        });
        if (book.barcode) seenBarcodes.add(book.barcode);
      }
    });
    
    return allBooks;
  };

  // Count total books for a display
  const getTotalBookCount = (display) => {
    return getAllBooksForDisplay(display).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        topic: formData.topic === "custom" ? customTopic : formData.topic
      };

      if (isEditing && selectedDisplay) {
        await axios.put(`${API}/book-displays/${selectedDisplay.id}`, submitData);
        toast.success("Book display updated successfully");
      } else {
        const response = await axios.post(`${API}/book-displays`, submitData);
        setSelectedDisplay(response.data);
        toast.success("Book display created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchDisplays();
    } catch (error) {
      toast.error(isEditing ? "Failed to update book display" : "Failed to create book display");
    }
  };

  const handleDelete = async () => {
    if (!selectedDisplay) return;
    try {
      await axios.delete(`${API}/book-displays/${selectedDisplay.id}`);
      toast.success("Book display deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedDisplay(null);
      fetchDisplays();
    } catch (error) {
      toast.error("Failed to delete book display");
    }
  };

  const handleExportExcel = async (displayId) => {
    try {
      const response = await axios.get(`${API}/book-displays/${displayId}/export/excel`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'book_display.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel file downloaded successfully");
    } catch (error) {
      toast.error("Failed to export book display");
    }
  };

  const handleEdit = (display) => {
    setSelectedDisplay(display);
    const isCustomTopic = display.topic && !DISPLAY_TOPICS.includes(display.topic);
    setFormData({
      title: display.title,
      topic: isCustomTopic ? "custom" : (display.topic || ""),
      theme: display.theme || "",
      description: display.description,
      location: display.location,
      start_date: display.start_date,
      end_date: display.end_date,
      linked_goals: display.linked_goals || [],
      notes: display.notes || ""
    });
    if (isCustomTopic) {
      setCustomTopic(display.topic);
    }
    setIsEditing(true);
    setFormTab("info");
    setDialogOpen(true);
  };

  const handleAddBook = async (e) => {
    e.preventDefault();
    if (!selectedDisplay) return;
    
    try {
      await axios.post(`${API}/book-displays/${selectedDisplay.id}/books`, bookData);
      toast.success("Book added to display");
      setAddBookDialogOpen(false);
      setBookData(initialBookData);
      fetchDisplays();
      // Refresh selected display
      const updated = await axios.get(`${API}/book-displays/${selectedDisplay.id}`);
      setSelectedDisplay(updated.data);
    } catch (error) {
      toast.error("Failed to add book to display");
    }
  };

  const handleRemoveBook = async (bookEntryId) => {
    if (!selectedDisplay) return;
    try {
      await axios.delete(`${API}/book-displays/${selectedDisplay.id}/books/${bookEntryId}`);
      toast.success("Book removed from display");
      fetchDisplays();
      // Refresh selected display
      const updated = await axios.get(`${API}/book-displays/${selectedDisplay.id}`);
      setSelectedDisplay(updated.data);
    } catch (error) {
      toast.error("Failed to remove book");
    }
  };

  const handleSelectExistingBook = (book) => {
    setBookData({
      ...bookData,
      title: book.title,
      author: book.author,
      call_number: book.call_number || "",
      barcode: book.barcode || "",
      branch: book.branch || "",
      member: book.member || "",
      book_id: book.id
    });
  };

  // File Upload Functions
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedDisplay) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/book-displays/${selectedDisplay.id}/attachments`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      toast.success(`${files.length} file(s) uploaded successfully`);
      fetchDisplays();
      // Refresh selected display
      const updated = await axios.get(`${API}/book-displays/${selectedDisplay.id}`);
      setSelectedDisplay(updated.data);
    } catch (error) {
      toast.error("Failed to upload file(s)");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCameraCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedDisplay) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/book-displays/${selectedDisplay.id}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Photo captured and uploaded");
      fetchDisplays();
      const updated = await axios.get(`${API}/book-displays/${selectedDisplay.id}`);
      setSelectedDisplay(updated.data);
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!selectedDisplay) return;
    try {
      await axios.delete(`${API}/book-displays/${selectedDisplay.id}/attachments/${attachmentId}`);
      toast.success("Attachment deleted");
      fetchDisplays();
      const updated = await axios.get(`${API}/book-displays/${selectedDisplay.id}`);
      setSelectedDisplay(updated.data);
    } catch (error) {
      toast.error("Failed to delete attachment");
    }
  };

  const isImageFile = (fileType) => {
    return fileType?.startsWith("image/");
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedDisplay(null);
    setIsEditing(false);
    setFormTab("info");
    setCustomTopic("");
  };

  // Barcode Scanner Functions
  const startScanner = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setScannerOpen(true);
    } catch (error) {
      toast.error("Unable to access camera. Please enter barcode manually.");
    }
  };

  const stopScanner = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScannerOpen(false);
  };

  const captureBarcode = async () => {
    const barcode = prompt("Enter the barcode you see in the camera:");
    if (barcode) {
      setBookData({ ...bookData, barcode: barcode.trim() });
      toast.success("Barcode captured");
    }
    stopScanner();
  };

  const isDisplayActive = (display) => {
    const today = new Date().toISOString().split('T')[0];
    return display.start_date <= today && display.end_date >= today;
  };

  const filteredDisplays = displays.filter((display) => {
    return display.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (display.topic || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (display.theme || "").toLowerCase().includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="displays-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="book-displays-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
            Book Displays
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage themed book displays
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          data-testid="create-display-btn"
          className="btn-animate"
        >
          <Plus className="mr-2 h-4 w-4" /> New Display
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search displays..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-displays"
        />
      </div>

      {/* Displays Grid */}
      {filteredDisplays.length === 0 ? (
        <Card className="border-stone-200" data-testid="no-displays">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Layout className="h-12 w-12 text-muted-foreground mb-4" strokeWidth={1.5} />
            <h3 className="font-medium text-lg">No book displays found</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {searchQuery ? "Try adjusting your search" : "Create your first book display"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDisplays.map((display, index) => (
            <Card
              key={display.id}
              className="card-hover border-stone-200 animate-slide-up"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`display-card-${display.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg line-clamp-2">
                      {display.title}
                    </CardTitle>
                    {display.topic && (
                      <Badge variant="secondary" className="mt-1 text-xs">
                        {display.topic}
                      </Badge>
                    )}
                    {getStaffName(display.created_by) && (
                      <p className="text-xs text-blue-600 mt-1">
                        Created by: {getStaffName(display.created_by)}
                      </p>
                    )}
                  </div>
                  {canEditDisplay(display) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`display-menu-${display.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleEdit(display)}
                        data-testid={`edit-display-${display.id}`}
                      >
                        <Pencil className="mr-2 h-4 w-4" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedDisplay(display);
                          setAddBookDialogOpen(true);
                        }}
                        data-testid={`add-book-to-display-${display.id}`}
                      >
                        <BookOpen className="mr-2 h-4 w-4" /> Add Book
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleExportExcel(display.id)}
                        data-testid={`export-display-${display.id}`}
                      >
                        <FileSpreadsheet className="mr-2 h-4 w-4" /> Export to Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => {
                          setSelectedDisplay(display);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-display-${display.id}`}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                  {display.description}
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span className="font-mono text-xs">
                      {display.start_date} - {display.end_date}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Layout className="h-4 w-4" />
                    <span>{display.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <BookMarked className="h-4 w-4" />
                    <span>
                      {getTotalBookCount(display)} books
                    </span>
                  </div>
                  {display.attachments && display.attachments.length > 0 && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Image className="h-4 w-4" />
                      <span>{display.attachments.length} photo(s)</span>
                    </div>
                  )}
                </div>
                
                {/* Display photos preview */}
                {display.attachments && display.attachments.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <p className="text-xs text-muted-foreground mb-2">Photos:</p>
                    <div className="flex gap-2 overflow-x-auto">
                      {display.attachments.slice(0, 3).map((att) => (
                        <div key={att.id} className="flex-shrink-0">
                          {isImageFile(att.file_type) ? (
                            <img
                              src={getAttachmentUrl(att)}
                              alt={att.original_name}
                              className="h-12 w-12 object-cover rounded"
                            />
                          ) : (
                            <div className="h-12 w-12 bg-muted flex items-center justify-center rounded">
                              <FileText className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      ))}
                      {display.attachments.length > 3 && (
                        <div className="h-12 w-12 bg-muted flex items-center justify-center rounded text-xs text-muted-foreground">
                          +{display.attachments.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Display books preview - includes both direct and linked books */}
                {getTotalBookCount(display) > 0 && (
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <p className="text-xs text-muted-foreground mb-2">Books in display:</p>
                    <div className="space-y-1">
                      {getAllBooksForDisplay(display).slice(0, 3).map((book, i) => (
                        <p key={book.id || i} className="text-xs truncate flex items-center gap-1">
                          {book.title} - {book.author}
                          {book.fromBooksPage && (
                            <span className="text-[10px] text-blue-500">(linked)</span>
                          )}
                        </p>
                      ))}
                      {getTotalBookCount(display) > 3 && (
                        <p className="text-xs text-muted-foreground">
                          +{getTotalBookCount(display) - 3} more
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between">
                  <Badge className={isDisplayActive(display) ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-600"}>
                    {isDisplayActive(display) ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Display Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {isEditing ? "Edit Book Display" : "Create New Book Display"}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? "Update the display details" : "Set up a new themed book display"}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={formTab} onValueChange={setFormTab} className="w-full">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="info">Display Info</TabsTrigger>
              <TabsTrigger value="books" disabled={!isEditing}>
                Books {isEditing && selectedDisplay && `(${getTotalBookCount(selectedDisplay)})`}
              </TabsTrigger>
              <TabsTrigger value="photos" disabled={!isEditing}>
                Photos {isEditing && `(${selectedDisplay?.attachments?.length || 0})`}
              </TabsTrigger>
            </TabsList>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <TabsContent value="info" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Display Information</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="title">Display Title *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Display title"
                        required
                        data-testid="display-title-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="topic">Display Topic</Label>
                      <Select
                        value={formData.topic || "none"}
                        onValueChange={(value) => setFormData({ ...formData, topic: value === "none" ? "" : value })}
                      >
                        <SelectTrigger data-testid="display-topic-select">
                          <SelectValue placeholder="Select a topic" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Select Topic --</SelectItem>
                          {DISPLAY_TOPICS.map((topic) => (
                            <SelectItem key={topic} value={topic}>
                              {topic}
                            </SelectItem>
                          ))}
                          <SelectItem value="custom">+ Create New Topic</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.topic === "custom" && (
                        <Input
                          className="mt-2"
                          value={customTopic}
                          onChange={(e) => setCustomTopic(e.target.value)}
                          placeholder="Enter custom topic"
                          data-testid="custom-topic-input"
                        />
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="theme">Theme (Optional)</Label>
                      <Input
                        id="theme"
                        value={formData.theme}
                        onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                        placeholder="e.g., Summer Reading, Black History Month"
                        data-testid="display-theme-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="description">Description *</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Describe the display..."
                        rows={3}
                        required
                        data-testid="display-description-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="location">Location *</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        placeholder="e.g., Main Entrance, Children's Section"
                        required
                        data-testid="display-location-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Duration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_date">Start Date *</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        required
                        data-testid="display-start-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_date">End Date *</Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        required
                        data-testid="display-end-date-input"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                    data-testid="display-notes-input"
                  />
                </div>

                {/* Linked Goals Section */}
                <div className="form-section">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Linked Goals
                  </h3>
                  
                  {/* Selected Goals */}
                  {formData.linked_goals.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.linked_goals.map((goalId) => {
                        const goal = goals.find(g => g.id === goalId);
                        return goal ? (
                          <Badge
                            key={goalId}
                            className="px-3 py-1 bg-emerald-100 text-emerald-700"
                          >
                            {goal.title}
                            <button
                              type="button"
                              onClick={() => setFormData({
                                ...formData,
                                linked_goals: formData.linked_goals.filter(id => id !== goalId)
                              })}
                              className="ml-2 hover:text-red-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                  
                  {/* Goals Dropdown */}
                  {goals.length > 0 ? (
                    <>
                      <p className="text-xs text-muted-foreground mb-2">Select goals to link:</p>
                      <div className="flex flex-wrap gap-2">
                        {goals.filter(goal => !formData.linked_goals.includes(goal.id)).map((goal) => (
                          <Badge
                            key={goal.id}
                            variant="outline"
                            className="cursor-pointer hover:bg-emerald-50 hover:border-emerald-300"
                            onClick={() => setFormData({
                              ...formData,
                              linked_goals: [...formData.linked_goals, goal.id]
                            })}
                            data-testid={`link-goal-${goal.id}`}
                          >
                            + {goal.title}
                          </Badge>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">No yearly goals available. Create goals in the Goals page first.</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="books" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Books in Display</h3>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAddBookDialogOpen(true)}
                    data-testid="add-book-btn"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Book
                  </Button>
                </div>
                
                {selectedDisplay && getTotalBookCount(selectedDisplay) > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title / Author</TableHead>
                        <TableHead>Barcode</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Purpose</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getAllBooksForDisplay(selectedDisplay).map((book) => (
                        <TableRow key={book.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{book.title}</p>
                              <p className="text-xs text-muted-foreground">{book.author}</p>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {book.barcode || book.call_number || "-"}
                          </TableCell>
                          <TableCell>
                            {book.fromBooksPage ? (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                Books Page
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-600 border-emerald-200">
                                Direct Add
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className="text-xs bg-blue-100 text-blue-700">
                              {PURPOSES.find(p => p.value === book.purpose)?.label || book.purpose || "Display"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {!book.fromBooksPage && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveBook(book.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 bg-muted/20 rounded-lg">
                    <BookOpen className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No books added yet</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Add books here or assign this display to books from the Books page
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="photos" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Photos & Files</h3>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={handleFileUpload}
                    />
                    <input
                      type="file"
                      ref={cameraInputRef}
                      className="hidden"
                      accept="image/*"
                      capture="environment"
                      onChange={handleCameraCapture}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => cameraInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="take-photo-btn"
                    >
                      <Camera className="mr-2 h-4 w-4" /> Take Photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-file-btn"
                    >
                      <Upload className="mr-2 h-4 w-4" /> Upload
                    </Button>
                  </div>
                </div>

                {uploading && (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2"></div>
                    <span className="text-sm text-muted-foreground">Uploading...</span>
                  </div>
                )}
                
                {selectedDisplay?.attachments && selectedDisplay.attachments.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {selectedDisplay.attachments.map((att) => (
                      <div key={att.id} className="relative group">
                        <div className="border rounded-lg overflow-hidden bg-muted/20">
                          {isImageFile(att.file_type) ? (
                            <img
                              src={getAttachmentUrl(att)}
                              alt={att.original_name}
                              className="w-full h-32 object-cover"
                            />
                          ) : (
                            <div className="w-full h-32 flex flex-col items-center justify-center">
                              <FileText className="h-10 w-10 text-muted-foreground mb-2" />
                              <span className="text-xs text-muted-foreground truncate px-2 max-w-full">
                                {att.original_name}
                              </span>
                            </div>
                          )}
                          <div className="p-2 border-t">
                            <p className="text-xs truncate">{att.original_name}</p>
                            <p className="text-xs text-muted-foreground">{formatFileSize(att.file_size)}</p>
                          </div>
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <a
                            href={getAttachmentUrl(att)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 bg-white rounded-full shadow hover:bg-gray-100"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                          <button
                            type="button"
                            onClick={() => handleDeleteAttachment(att.id)}
                            className="p-1.5 bg-white rounded-full shadow hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-muted/20 rounded-lg">
                    <Image className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">No photos or files yet</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Take a photo or upload files to document this display
                    </p>
                  </div>
                )}
              </TabsContent>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-display-btn">
                  {isEditing ? "Update Display" : "Create Display"}
                </Button>
              </DialogFooter>
            </form>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Add Book to Display Dialog */}
      <Dialog open={addBookDialogOpen} onOpenChange={setAddBookDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Add Book to Display
            </DialogTitle>
            <DialogDescription>
              Add a book to "{selectedDisplay?.title}"
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddBook} className="space-y-6">
            {/* Select from existing books */}
            <div className="form-section">
              <h3 className="font-medium mb-3">Select from Library</h3>
              <Select
                value={bookData.book_id || "none"}
                onValueChange={(value) => {
                  if (value !== "none") {
                    const book = books.find(b => b.id === value);
                    if (book) handleSelectExistingBook(book);
                  } else {
                    setBookData(initialBookData);
                  }
                }}
              >
                <SelectTrigger data-testid="select-existing-book">
                  <SelectValue placeholder="Choose existing book or enter new" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Enter New Book --</SelectItem>
                  {books.map((book) => (
                    <SelectItem key={book.id} value={book.id}>
                      {book.title} - {book.author}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-section">
              <h3 className="font-medium mb-3">Book Information</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="book_title">Title *</Label>
                    <Input
                      id="book_title"
                      value={bookData.title}
                      onChange={(e) => setBookData({ ...bookData, title: e.target.value })}
                      placeholder="Book title"
                      required
                      data-testid="book-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="book_author">Author *</Label>
                    <Input
                      id="book_author"
                      value={bookData.author}
                      onChange={(e) => setBookData({ ...bookData, author: e.target.value })}
                      placeholder="Author name"
                      required
                      data-testid="book-author-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="book_call_number">Call Number</Label>
                    <Input
                      id="book_call_number"
                      value={bookData.call_number}
                      onChange={(e) => setBookData({ ...bookData, call_number: e.target.value })}
                      placeholder="e.g., 813.54 SMI"
                      data-testid="book-call-number-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="book_barcode">Barcode</Label>
                    <div className="flex gap-2">
                      <Input
                        id="book_barcode"
                        value={bookData.barcode}
                        onChange={(e) => setBookData({ ...bookData, barcode: e.target.value })}
                        placeholder="Scan or enter barcode"
                        data-testid="book-barcode-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={startScanner}
                        data-testid="scan-barcode-btn"
                      >
                        <Camera className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="font-medium mb-3">Location</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="book_branch">Branch</Label>
                  <Select
                    value={bookData.branch || "none"}
                    onValueChange={(value) => setBookData({ ...bookData, branch: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="book-branch-select">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select Branch --</SelectItem>
                      {BRANCHES.map((branch) => (
                        <SelectItem key={branch} value={branch}>
                          {branch}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="book_member">Member</Label>
                  <Select
                    value={bookData.member || "none"}
                    onValueChange={(value) => setBookData({ ...bookData, member: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="book-member-select">
                      <SelectValue placeholder="Select member" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select Member --</SelectItem>
                      {MEMBERS.map((member) => (
                        <SelectItem key={member} value={member}>
                          {member}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="font-medium mb-3">Purpose</h3>
              <Select
                value={bookData.purpose || "book_display"}
                onValueChange={(value) => setBookData({ ...bookData, purpose: value })}
              >
                <SelectTrigger data-testid="book-purpose-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PURPOSES.map((purpose) => (
                    <SelectItem key={purpose.value} value={purpose.value}>
                      {purpose.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setAddBookDialogOpen(false);
                setBookData(initialBookData);
              }}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-book-btn">
                Add Book
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Barcode Scanner Dialog */}
      <Dialog open={scannerOpen} onOpenChange={(open) => !open && stopScanner()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Scan Barcode</DialogTitle>
            <DialogDescription>
              Point your camera at the barcode
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-24 border-2 border-white/50 rounded-lg">
                  <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-red-500 animate-pulse" />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={stopScanner} className="flex-1">
                Cancel
              </Button>
              <Button onClick={captureBarcode} className="flex-1" data-testid="capture-barcode-btn">
                <Camera className="mr-2 h-4 w-4" /> Capture
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Book Display</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedDisplay?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-display"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
